import { useEffect, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

interface BottomSheetProps {
  /** Si está abierto */
  open: boolean;
  /** Callback al cerrar (overlay click, swipe down, ESC, botón X) */
  onClose: () => void;
  /** Contenido del sheet */
  children: ReactNode;
  /** Título opcional en la parte superior */
  title?: string;
  /**
   * Altura del sheet:
   *   - "auto": se ajusta al contenido (max 85vh)
   *   - "half": 50vh
   *   - "full": 92vh
   * Default: "auto"
   */
  height?: "auto" | "half" | "full";
  /** Permite cerrar con swipe down (default true) */
  swipeToClose?: boolean;
  /** ClassName extra para el contenedor del contenido */
  contentClassName?: string;
}

/**
 * BottomSheet — drawer modal que se desliza desde abajo.
 *
 * Patrón estándar de mobile (iOS sheets, Material bottom sheets).
 * En desktop se puede usar también pero el patrón habitual es panel lateral.
 *
 * Características:
 *   - Drag handle visible arriba para indicar interactividad
 *   - Overlay oscuro detrás (click cierra)
 *   - Cierre con ESC
 *   - Cierre con swipe down (si swipeToClose=true)
 *   - Sin librerías externas (CSS transitions puras)
 *   - Body scroll lock mientras está abierto
 *
 * Uso:
 *   <BottomSheet open={detailOpen} onClose={() => setDetailOpen(false)} title="Detalle del lead">
 *     <ConversationContext ... />
 *   </BottomSheet>
 */
export function BottomSheet({
  open,
  onClose,
  children,
  title,
  height = "auto",
  swipeToClose = true,
  contentClassName,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartY = useRef<number | null>(null);

  // ESC para cerrar
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Body scroll lock cuando está abierto
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Reset drag offset cuando cierra
  useEffect(() => {
    if (!open) {
      setDragOffset(0);
      dragStartY.current = null;
    }
  }, [open]);

  /* --- Touch handlers para swipe-to-close --- */

  function handleTouchStart(e: React.TouchEvent) {
    if (!swipeToClose) return;
    dragStartY.current = e.touches[0].clientY;
  }

  function handleTouchMove(e: React.TouchEvent) {
    if (!swipeToClose || dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) {
      setDragOffset(delta);
    }
  }

  function handleTouchEnd() {
    if (!swipeToClose || dragStartY.current === null) return;
    // Si arrastró más de 100px hacia abajo, cierra
    if (dragOffset > 100) {
      onClose();
    } else {
      // Snap back a 0
      setDragOffset(0);
    }
    dragStartY.current = null;
  }

  const heightClass = {
    auto: "max-h-[85vh]",
    half: "h-[50vh]",
    full: "h-[92vh]",
  }[height];

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300",
          open ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "bottom-sheet-title" : undefined}
        className={cn(
          "fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-2xl border-t border-border bg-surface shadow-2xl transition-transform duration-300 ease-out",
          heightClass,
          open ? "translate-y-0" : "translate-y-full"
        )}
        style={{
          // Aplicar drag offset solo cuando está siendo arrastrado
          transform: open
            ? `translateY(${dragOffset}px)`
            : undefined,
          transition: dragStartY.current !== null ? "none" : undefined,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag handle visual */}
        <div className="flex justify-center pt-2.5 pb-1">
          <div
            className="h-1 w-10 rounded-full bg-border-strong"
            aria-hidden
          />
        </div>

        {/* Header (si hay título) */}
        {title && (
          <div className="flex items-center justify-between border-b border-border px-4 pb-3 pt-1">
            <h2
              id="bottom-sheet-title"
              className="text-base font-semibold text-foreground"
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-surface-2 hover:text-foreground"
              aria-label="Cerrar"
            >
              <X className="size-4" />
            </button>
          </div>
        )}

        {/* Contenido scrollable */}
        <div
          className={cn(
            "flex-1 overflow-y-auto overscroll-contain",
            contentClassName
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
}
